import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Grid,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { Suspense, useMemo } from 'react';
import LoadingIndicator from '../../../components/indicators/LoadingIndicator';

const formatEnergyUnit = (value) => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000)?.toFixed(1) + ' GWh';
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000)?.toFixed(1) + ' MWh';
  } else {
    return value.toFixed(0) + ' kWh';
  }
};

const renderEnergyCard = (type, icon, energyData, co2Data) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <Paper
        variant="standard"
        elevation={0}
        sx={{
          p: 2,
          display: 'flex',
          gap: 2,
          width: '100%',
          boxShadow: '0 20px 27px 0 rgb(0 0 0 / 5%)',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {icon}
              <Typography component="div" variant="body1" color="text.primary">
                {type === 'Heat' ? 'Fjernvarme' : type === 'Gas' ? 'Gas' : 'El'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SpeedRoundedIcon />
              <Typography
                component="div"
                variant="body1"
                color="text.primary"
                sx={{ ml: 1 }}
              >
                {energyData?.BaseUnit === 'kWh'
                  ? formatEnergyUnit(energyData?.TotalEnergyUsage)
                  : `${energyData?.TotalEnergyUsage.toFixed(1)} ${
                      energyData?.BaseUnit
                    }`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SavingsRoundedIcon />
              <Typography
                component="div"
                variant="body1"
                color="text.primary"
                sx={{ ml: 1 }}
              >
                {energyData?.BaseUnit === 'kWh'
                  ? formatEnergyUnit(energyData?.TotalEnergySavings)
                  : `${energyData?.TotalEnergySavings.toFixed(1)} ${
                      energyData?.BaseUnit
                    }`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Co2RoundedIcon />
              <Typography
                component="div"
                variant="body1"
                color="text.primary"
                sx={{ ml: 1 }}
              >
                {co2Data?.TotalCO2Savings !== undefined &&
                  (Math.abs(co2Data.TotalCO2Savings) >= 1000000
                    ? `${(co2Data.TotalCO2Savings / 1000000).toFixed(1)} t CO2e`
                    : Math.abs(co2Data.TotalCO2Savings) >= 1000
                    ? `${(co2Data.TotalCO2Savings / 1000).toFixed(1)} kg CO2e`
                    : `${co2Data.TotalCO2Savings.toFixed(0)} g CO2e`)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  </Grid>
);

const SavingsSummaryCards = ({
  energySavings,
  baselineDays,
  isTodayInSeason,
  daysUntilNextSeason,
  isLoading,
  loadingMessage,
}) => {
  const heatCO2Savings = energySavings?.HeatCO2Savings || {};
  const gasCO2Savings = energySavings?.GasCO2Savings || {};
  const electricityCO2Savings = energySavings?.ElectricityCO2Savings || {};
  const { Heat, Gas, Electricity, EnergySavingsSummary, CO2SavingsSummary } =
    energySavings || {};

  const energyCards = useMemo(
    () =>
      [
        Heat?.TotalEnergyUsage !== 0 &&
          renderEnergyCard(
            'Heat',
            <MenuRoundedIcon sx={{ transform: 'rotate(90deg)', mr: 0.5 }} />,
            Heat,
            heatCO2Savings,
          ),
        Gas?.TotalEnergyUsage !== 0 &&
          renderEnergyCard(
            'Gas',
            <LocalFireDepartmentRoundedIcon sx={{ mr: 1 }} />,
            Gas,
            gasCO2Savings,
          ),
        Electricity?.TotalEnergyUsage !== 0 &&
          renderEnergyCard(
            'Electricity',
            <BoltRoundedIcon sx={{ mr: 0.5 }} />,
            Electricity,
            electricityCO2Savings,
          ),
      ].filter(Boolean),
    [
      Heat,
      Gas,
      Electricity,
      heatCO2Savings,
      gasCO2Savings,
      electricityCO2Savings,
    ],
  );

  if (isLoading) {
    return (
      <LoadingIndicator show={isLoading} loadingMessage={loadingMessage} />
    );
  }

  return (
    <Suspense
      fallback={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '80vh',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 4 }}>
            {loadingMessage}
          </Typography>
        </Box>
      }
    >
      <Grid container spacing={2}>
        {energySavings && EnergySavingsSummary ? (
          <>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  flex: 1,
                  width: '100%',
                }}
              >
                <ListItemText
                  primary="Samlet overblik"
                  primaryTypographyProps={{
                    variant: 'h5',
                    color: 'text.primary',
                    fontWeight: 700,
                  }}
                  secondary="Det samlede overblik over besparelser på energiforbrug og CO2-udledning på tværs af bygningsporteføljen."
                  sx={{ mb: 2 }}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'text.primary', fontWeight: 'bold' }}
                  >
                    Forsyning
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'text.primary', fontWeight: 'bold' }}
                  >
                    Aflæst forbrug
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'text.primary', fontWeight: 'bold' }}
                  >
                    Besparelse
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'text.primary', fontWeight: 'bold' }}
                  >
                    CO2 Besparelse
                  </Typography>
                </Grid>
              </Grid>
              {energyCards.length > 0 && <>{energyCards}</>}

              <Box sx={{ textAlign: { sm: 'left', md: 'center' }, py: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ textAlign: 'left', color: 'text.secondary', my: 2 }}
                >
                  Den samlede besparelse på energiforbruget fra varme og den
                  tilhørende CO2-reduktion for alle bygninger tilknyttet denne
                  konto vises her. I kan dykke ned i dataen for den enkelte
                  bygning, ved at klikke ind på den. Besparelsen bliver beregnet
                  løbende, og ændres i takt med, at der kommer mere og mere
                  data. Især i starten af sæsonen hvor varmeforbruget er
                  begrænset og der er få baseline dage at sammenligne med, kan
                  der være store udsving. Det stabiliserer sig når
                  varmeforbruget øges i takt med at det bliver koldere, og der
                  bliver flere baseline dage at sammenligne med. En varmesæson
                  går fra 1. oktober til 31. maj kl. 12. Vi bruger altid nyeste
                  tilgængelige målerdata.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {/* Summary Card: Total Energy Savings Percentage */}
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="standard"
                    elevation={0}
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 20px 27px 0 rgb(0 0 0 / 5%)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography
                        component="h2"
                        variant="body1"
                        color="text.secondary"
                      >
                        Gennemsnitlig Besparelse{' '}
                        {baselineDays?.length && baselineDays?.length <= 10 && (
                          <Typography
                            component="i"
                            variant="body2"
                            color="text.secondary"
                          >
                            *
                          </Typography>
                        )}
                      </Typography>
                      <Tooltip
                        title="Besparelsesprocenten er baseret på forholdet mellem gennemsnitsforbruget på dage uden optimering (baselinedage) og det faktiske forbrug. Der bruges her et vægtet gennemsnit på tværs af alle bygninger tilknyttet denne konto."
                        placement="top"
                        arrow
                      >
                        <InfoRoundedIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                          fontSize="small"
                        />
                      </Tooltip>
                    </Box>
                    <Typography component="p" variant="h5">
                      {EnergySavingsSummary?.TotalEnergySavingsPercentage?.toFixed(
                        1,
                      )}{' '}
                      %
                    </Typography>
                  </Paper>
                </Grid>

                {/* Summary Card: Total Energy Savings */}
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="standard"
                    elevation={0}
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 20px 27px 0 rgb(0 0 0 / 5%)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography
                        component="h2"
                        variant="body1"
                        color="text.secondary"
                      >
                        Samlet Besparelse
                      </Typography>
                      <Tooltip
                        title="Den samlede besparelse er forskellen mellem det beregnede forbrug uden Prædiktiv Varmeoptimering og det aflæste forbrug i perioden. Det beregnede forbrug uden Prædiktiv Varmeoptimering for hver bygning kan findes ved at klikke ind på pågældende bygning. "
                        placement="top"
                        arrow
                      >
                        <InfoRoundedIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                          fontSize="small"
                        />
                      </Tooltip>
                    </Box>
                    <Typography component="p" variant="h5">
                      {EnergySavingsSummary?.TotalEnergySavings >= 1000000 ||
                      EnergySavingsSummary?.TotalEnergySavings <= -1000000
                        ? (
                            EnergySavingsSummary?.TotalEnergySavings / 1000000
                          ).toFixed(1) + ' GWh'
                        : EnergySavingsSummary?.TotalEnergySavings >= 1000 ||
                          EnergySavingsSummary?.TotalEnergySavings <= -1000
                        ? (
                            EnergySavingsSummary?.TotalEnergySavings / 1000
                          ).toFixed(1) + ' MWh'
                        : EnergySavingsSummary?.TotalEnergySavings.toFixed(0) +
                          ' kWh'}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Summary Card: Total CO2 Savings */}
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="standard"
                    elevation={0}
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 20px 27px 0 rgb(0 0 0 / 5%)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography
                        component="h2"
                        variant="body1"
                        color="text.secondary"
                      >
                        Samlet CO<sub>2</sub> Besparelse
                      </Typography>
                      <Tooltip
                        title="Besparelsen er forholdet mellem forbruget, hvis alle dage var uden optimering (baselinedage) set i forhold til det faktiske forbrug."
                        placement="top"
                        arrow
                      >
                        <InfoRoundedIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                          fontSize="small"
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography component="p" variant="h5">
                        {CO2SavingsSummary?.TotalCO2Savings >= 1000000 ||
                        CO2SavingsSummary?.TotalCO2Savings <= -1000000
                          ? (
                              CO2SavingsSummary?.TotalCO2Savings / 1000000
                            ).toFixed(1) + ' t CO2e'
                          : CO2SavingsSummary?.TotalCO2Savings >= 1000 ||
                            CO2SavingsSummary?.TotalCO2Savings <= -1000
                          ? (CO2SavingsSummary?.TotalCO2Savings / 1000).toFixed(
                              1,
                            ) + ' kg CO2e'
                          : CO2SavingsSummary?.TotalCO2Savings.toFixed(0) +
                            ' g CO2e'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Summary Card: Total Energy Usage */}
                <Grid item xs={6} md={3}>
                  <Paper
                    variant="standard"
                    elevation={0}
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 20px 27px 0 rgb(0 0 0 / 5%)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography
                        component="h2"
                        variant="body1"
                        color="text.secondary"
                      >
                        Total Forbrug
                      </Typography>
                      <Tooltip
                        title="Totalforbruget er det aflæste forbrug direkte fra forsyningsselskabet, afregningsmåleren eller hovedmåleren."
                        placement="top"
                      >
                        <InfoRoundedIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                          fontSize="small"
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography component="p" variant="h5">
                        {EnergySavingsSummary?.TotalEnergyUsage >= 1000000 ||
                        EnergySavingsSummary?.TotalEnergyUsage <= -1000000
                          ? (
                              EnergySavingsSummary?.TotalEnergyUsage / 1000000
                            ).toFixed(1) + ' GWh'
                          : EnergySavingsSummary?.TotalEnergyUsage >= 1000 ||
                            EnergySavingsSummary?.TotalEnergyUsage <= -1000
                          ? (
                              EnergySavingsSummary?.TotalEnergyUsage / 1000
                            ).toFixed(1) + ' MWh'
                          : EnergySavingsSummary?.TotalEnergyUsage.toFixed(0) +
                            ' kWh'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Baseline Day Warning */}
              {baselineDays?.length && baselineDays?.length <= 10 && (
                <Typography
                  component="i"
                  variant="body2"
                  color="text.secondary"
                >
                  * Der har været under 10 baseline-dage, og beregningen af
                  besparelsen kan derfor fortsat have store udsving.
                </Typography>
              )}
              {/* Additional Summary Info */}
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Alert severity="info">
              <AlertTitle sx={{ fontWeight: 'bold' }}>
                Besparelsen kan endnu ikke vises
              </AlertTitle>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Typography variant="subtitle1">
                  Der er endnu ingen besparelsesdata at vise baseret på
                  Prædiktiv Varmeoptimering. Dette kan skyldes manglende
                  målerdata, udestående data eller at der ikke har været
                  tilstrækkeligt med baseline-dage til at beregne en besparelse.
                  Ligeså snart data er tilstrækkelig, vil besparelsen fremgå.
                  Har du spørgsmål eller undrer dig, så kontakt os f.eks. på:{' '}
                  <a href="mailto:support@remoni.com">support@remoni.com</a>
                </Typography>
              </Box>
            </Alert>
          </Grid>
        )}
      </Grid>
    </Suspense>
  );
};

export default SavingsSummaryCards;
